import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TablePreviewCanvas } from './TablePreview';
import { ViewerControls } from './ViewerControls';
import { CustomizationOptions } from '../../types/customization';
import { Maximize2, Minimize2, Loader } from 'lucide-react';
import { cn } from '../../lib/utils';

interface EnhancedTablePreviewProps {
  options: CustomizationOptions;
  materialTexture: string;
  className?: string;
  initialFinish?: 'glossy' | 'matte';
  compact?: boolean;
  fullscreenEnabled?: boolean;
}

export const EnhancedTablePreview: React.FC<EnhancedTablePreviewProps> = ({
  options,
  materialTexture,
  className = '',
  initialFinish = 'glossy',
  compact = false,
  fullscreenEnabled = true
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeViewMode, setActiveViewMode] = useState('3d');
  const [activeFinish, setActiveFinish] = useState<'glossy' | 'matte'>(initialFinish);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showWireframe, setShowWireframe] = useState(false);
  const [showAxes, setShowAxes] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [activeEnvironment, setActiveEnvironment] = useState('apartment');
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('medium');
  
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Simulate loading state
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800); // Reduced loading time for better UX
    
    return () => clearTimeout(timer);
  }, [options.shape, options.edgeProfile, materialTexture]);
  
  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!fullscreenEnabled) return;
    
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if ((containerRef.current as any)?.webkitRequestFullscreen) {
        (containerRef.current as any).webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  };
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!(document as any).webkitFullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Detect device capabilities for quality settings
  useEffect(() => {
    const detectDeviceCapabilities = () => {
      // Check if device is mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Check GPU performance (rough estimate)
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (!gl) {
        // WebGL not supported, use low quality
        return 'low';
      }
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : '';
      
      // Check for high-end GPUs
      const isHighEnd = /(NVIDIA|RTX|GTX|AMD|Radeon)/i.test(renderer);
      
      if (isMobile) {
        return 'low';
      } else if (isHighEnd) {
        return 'high';
      } else {
        return 'medium';
      }
    };
    
    setQuality(detectDeviceCapabilities() as 'low' | 'medium' | 'high');
  }, []);
  
  // Implement viewer controls
  const viewerControls = {
    reset: () => {
      setActiveViewMode('3d');
    },
    takeScreenshot: async () => {
      // This would be implemented with actual canvas capture in a real implementation
      console.log('Taking screenshot');
      return '';
    },
    exportGLTF: async () => {
      console.log('Exporting GLTF');
      return new Blob();
    },
    toggleWireframe: () => setShowWireframe(!showWireframe),
    toggleAxes: () => setShowAxes(!showAxes),
    toggleMeasurements: () => setShowMeasurements(!showMeasurements),
    setEnvironment: (preset: string) => setActiveEnvironment(preset),
    setQuality: (q: 'low' | 'medium' | 'high') => setQuality(q),
    setFinish: (finish: 'glossy' | 'matte') => setActiveFinish(finish)
  };
  
  const handleEdgeSelect = () => {
    // This function is called when an edge is clicked in the 3D viewer
    console.log('Edge selected');
  };
  
  return (
    <div 
      ref={containerRef}
      className={cn(
        "relative bg-[#1C1C1C] rounded-xl overflow-hidden",
        isFullscreen ? "fixed inset-0 z-50" : "aspect-[16/10]",
        className
      )}
    >
      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-20"
          >
            <motion.div
              animate={{ 
                rotate: 360,
                transition: { duration: 2, repeat: Infinity, ease: "linear" }
              }}
            >
              <Loader className="w-10 h-10 text-gold" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* 3D Canvas */}
      <div className="w-full h-full">
        <TablePreviewCanvas
          options={options}
          materialTexture={materialTexture}
          finish={activeFinish}
          hideControls={true}
          showMeasurements={showMeasurements}
          quality={quality}
          environmentPreset={activeEnvironment}
          onEdgeSelect={handleEdgeSelect}
        />
      </div>
      
      {/* Controls Overlay */}
      <ViewerControls
        controls={viewerControls}
        state={{
          showWireframe,
          showAxes,
          showMeasurements,
          activeEnvironment,
          quality,
          finish: activeFinish,
          activeViewMode
        }}
        onViewChange={setActiveViewMode}
        onFinishChange={setActiveFinish}
        compact={compact}
      />
      
      {/* Fullscreen Toggle */}
      {fullscreenEnabled && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleFullscreen}
          className="absolute bottom-4 right-4 p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white z-10"
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize2 className="w-5 h-5" />
          ) : (
            <Maximize2 className="w-5 h-5" />
          )}
        </motion.button>
      )}
    </div>
  );
};